import { Component, Input, Output, EventEmitter, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RoomsService } from '../../../../core/services/rooms.service';
import { Room } from '../../../../core/models/room.model';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { take } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { toast } from 'ngx-sonner';

@Component({
    selector: 'app-room-form-modal',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule, 
        TranslateModule, 
        DialogModule, 
        InputTextModule, 
        InputNumberModule, 
        ToggleButtonModule,
        AppButton
    ],
    templateUrl: './room-form-modal.html',
})
export class RoomFormModal implements OnInit {
    @Input() room: Room | null = null;
    @Output() close = new EventEmitter<void>();
    @Output() saved = new EventEmitter<void>();

    roomForm: FormGroup;
    loading = false;
    isEditMode = false;

    private fb = inject(FormBuilder);
    private roomsService = inject(RoomsService);
    private store = inject(Store);
    private translate = inject(TranslateService);
    private cdr = inject(ChangeDetectorRef);
    private userInfo$ = this.store.select(selectUserInfo);

    constructor() {
        this.roomForm = this.fb.group({
            name: ['', [Validators.required]],
            location: ['', [Validators.required]],
            capacity: [null, [Validators.required, Validators.min(1)]],
            active: [true]
        });
    }

    ngOnInit() {
        if (this.room) {
            this.isEditMode = true;
            this.roomForm.patchValue({
                name: this.room.name,
                location: this.room.location,
                capacity: this.room.capacity,
                active: this.room.active !== false
            });
        }
    }

    onClose() {
        this.close.emit();
    }

    async onSave() {
        if (this.roomForm.invalid) {
            this.roomForm.markAllAsTouched();
            return;
        }

        this.loading = true;
        this.cdr.detectChanges();

        try {
            const userInfo = await this.userInfo$.pipe(take(1)).toPromise();
            const branchId = userInfo?.branches_id || userInfo?.branch_id;

            if (!branchId) {
                toast.error(this.translate.instant('common_error_branch_not_found'));
                this.loading = false;
                return;
            }

            const val = this.roomForm.value;
            const roomData: any = {
                name: val.name,
                location: val.location,
                capacity: val.capacity,
                active: val.active,
                branches_id: branchId
            };

            if (this.isEditMode && this.room) {
                const { error } = await this.roomsService.updateRoom(this.room.id, roomData);
                if (error) throw error;
                toast.success(this.translate.instant('rooms_save_success'));
            } else {
                const { error } = await this.roomsService.createRoom(roomData);
                if (error) throw error;
                toast.success(this.translate.instant('rooms_save_success'));
            }

            this.saved.emit();
            this.onClose();
        } catch (err) {
            console.error('Error saving room:', err);
            toast.error(this.translate.instant('rooms_save_error'));
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }
}
